import { useState, useRef, useEffect } from "react";
import { Search, User, CalendarCheck, Users as UsersIcon, Phone, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";

export default function AdminGlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { const r = await adminApi.get("/admin/search", { params: { q } }); setRes(r.data); setOpen(true); }
      catch { /* ignore */ } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);

  const goCustomer = (id) => { setOpen(false); setQ(""); navigate(`/admin/customers/${id}`); };
  const total = res ? res.customers.length + res.appointments.length + res.staff.length : 0;

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input data-testid="admin-global-search" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res && setOpen(true)}
        placeholder="Search customers, businesses, phones, appointments, staff…"
        className="w-full pl-9 pr-4 h-9 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" />
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
      {open && res && (
        <div data-testid="admin-search-results" className="absolute left-0 mt-2 w-[28rem] max-w-[90vw] bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden z-40 max-h-[70vh] overflow-y-auto">
          {total === 0 && <p className="px-4 py-6 text-sm text-slate-400 text-center">No matches</p>}
          {res.customers.length > 0 && <Group label="Customers"><>{res.customers.map((c) => (
            <button key={c.id} data-testid={`search-customer-${c.id}`} onClick={() => goCustomer(c.id)} className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2">
              <User className="h-4 w-4 text-blue-500" /><span className="text-sm font-medium">{c.business_name || c.name}</span><span className="text-xs text-slate-400 ml-auto">{c.email}</span>
            </button>))}</></Group>}
          {res.staff.length > 0 && <Group label="Staff"><>{res.staff.map((s) => (
            <button key={s.id} onClick={() => goCustomer(s.user_id)} className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2">
              <UsersIcon className="h-4 w-4 text-violet-500" /><span className="text-sm">{s.name}</span><span className="text-xs text-slate-400 ml-auto">{s.title}</span>
            </button>))}</></Group>}
          {res.appointments.length > 0 && <Group label="Appointments"><>{res.appointments.map((a) => (
            <button key={a.id} onClick={() => goCustomer(a.user_id)} className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2">
              <CalendarCheck className="h-4 w-4 text-emerald-500" /><span className="text-sm">{a.customer_name}</span><span className="text-xs text-slate-400 ml-auto">{a.service} · {a.when}</span>
            </button>))}</></Group>}
          {res.phone_numbers.length > 0 && <Group label="Phone numbers"><>{res.phone_numbers.map((p, i) => (
            <button key={i} onClick={() => goCustomer(p.user_id)} className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2">
              <Phone className="h-4 w-4 text-blue-500" /><span className="text-sm font-mono">{p.phone}</span><span className="text-xs text-slate-400 ml-auto">{p.business_name}</span>
            </button>))}</></Group>}
        </div>
      )}
    </div>
  );
}

const Group = ({ label, children }) => (
  <div><p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>{children}</div>
);
